import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, 
  Pencil, 
  Trash, 
  BookOpen
} from '@phosphor-icons/react';
import { programsApi, formatApiErrorDetail } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const programSchema = z.object({
  programCode: z.string().min(1, 'Program code is required').max(10, 'Max 10 characters'),
  programName: z.string().min(1, 'Program name is required'),
});

export default function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProgram, setEditProgram] = useState(null);
  const [deleteProgram, setDeleteProgram] = useState(null);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(programSchema),
  });

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const { data } = await programsApi.getAll();
      setPrograms(data);
    } catch (error) {
      console.error('Failed to load programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditProgram(null);
    reset({ programCode: '', programName: '' });
    setShowModal(true);
  };

  const openEditModal = (program) => {
    setEditProgram(program);
    reset(program);
    setShowModal(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      if (editProgram) {
        await programsApi.update(editProgram.id, data);
        toast.success('Program updated');
      } else {
        await programsApi.create(data);
        toast.success('Program created');
      }
      setShowModal(false);
      loadPrograms();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await programsApi.delete(deleteProgram.id);
      toast.success('Program deleted');
      setDeleteProgram(null);
      loadPrograms();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="overline mb-2">Configuration</p>
          <h1 className="text-3xl font-black tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Programs
          </h1>
        </div>
        <Button
          onClick={openCreateModal}
          data-testid="add-program-btn"
          className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none"
        >
          <Plus size={18} className="mr-2" />
          Add Program
        </Button>
      </div>

      {/* Programs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programs-grid">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E8E8E8] p-6 animate-pulse">
              <div className="h-6 bg-[#E8E8E8] w-20 mb-2" />
              <div className="h-4 bg-[#E8E8E8] w-40" />
            </div>
          ))
        ) : programs.length === 0 ? (
          <div className="col-span-full bg-white border border-[#E8E8E8] p-12 text-center">
            <BookOpen size={48} className="mx-auto mb-4 text-[#666666]" weight="duotone" />
            <p className="text-[#666666]">No programs found. Add your first program.</p>
          </div>
        ) : (
          programs.map((program, idx) => (
            <div 
              key={program.id} 
              className="bg-white border border-[#E8E8E8] p-6 hover:border-[#E31E25] transition-colors animate-fade-in-up"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="inline-block bg-[#E31E25] text-white px-2 py-1 text-xs font-bold mb-2">
                    {program.programCode}
                  </span>
                  <h3 className="font-semibold text-[#1A1A1A]">{program.programName}</h3>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEditModal(program)}
                    data-testid={`edit-program-${program.id}`}
                    className="p-2 hover:bg-[#E31E25]/10 text-[#E31E25] transition-colors"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteProgram(program)}
                    data-testid={`delete-program-${program.id}`}
                    className="p-2 hover:bg-[#E31E25]/10 text-[#E31E25] transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={() => setShowModal(false)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editProgram ? 'Edit Program' : 'Add Program'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Program Code</Label>
              <Input 
                {...register('programCode')} 
                className="mt-1 rounded-none uppercase"
                placeholder="e.g., BSCS"
                maxLength={10}
              />
              {errors.programCode && <p className="text-[#E31E25] text-xs mt-1">{errors.programCode.message}</p>}
            </div>
            <div>
              <Label className="text-sm font-semibold">Program Name</Label>
              <Input 
                {...register('programName')} 
                className="mt-1 rounded-none"
                placeholder="e.g., B.Sc Computer Science"
              />
              {errors.programName && <p className="text-[#E31E25] text-xs mt-1">{errors.programName.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none">
                {saving ? 'Saving...' : (editProgram ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProgram} onOpenChange={() => setDeleteProgram(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteProgram?.programName}? This may affect related student records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-[#E31E25] hover:bg-[#E31E25]/90 rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
